import React, {useState, useEffect} from "react"
import PropTypes from "prop-types"
import {withRouter, RouteComponentProps} from "react-router-dom"

import FormattedMessage, {injectIntl, InjectIntl, getMessage} from "@irati/utils/lang"
import Tabs from "@irati/ui/src/Tabs"
import {H1} from "@irati/ui/src/Typography"
import salesService from "@irati/sales/services/sales"
import buService from "@irati/billing/services/bu"
import {RenewalError, RenewalOption, RenewalQuoteStatus, ZuoraCurrentPlanWindow} from "@irati/types"
import intercom from "@irati/utils/intercom"
import authentication from "@irati/auth"
import ErrorText from "@irati/ui/src/ErrorText"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import History from "@irati/utils/history"
import Loading from "@irati/ui/src/Loading"
import adminTracking, {RenewalPlanType, RenewalPlanPage, RenewalPlanSubSource, RenewalPlanSource} from "@irati-tracking/admin/admin"
import {hangPromise} from "@irati/utils/promise"

import devToggles from "@irati/dev-toggles"
import Invoices from "../../components/Billing/Invoices"
import CurrentPlan, {CurrentPlanCallback} from "../../components/Billing/CurrentPlan"
import NewPlan from "../../components/Billing/RenewalQuoteSelection"
import RenewalStatus from "../../components/Billing/RenewalStatus"
import Wrapped from "../../components/Billing/YourPerkboxStory"

export enum BILLING_TABS {
  CURRENT_PLAN = "currentPlan",
  NEW_PLAN = "renewalPlan",
  RENEWAL_STATUS = "renewalStatus",
  PAST_INVOICES = "invoices",
  YOUR_PERKBOX_STORY = "yourPerkboxStory"
}

export interface HistoryState {
  prevUrl?: string
  winbackOption?: RenewalOption
}

type Props = InjectIntl & RouteComponentProps<null, any, HistoryState>

const Billing: React.FC<Props> = ({location, formatMessage}, context) => {
  const [loaded, setLoaded] = useState(hangPromise<any>())
  const [selectedKey, setSelectedKey] = useState<string>(window.location.pathname.split("billing/")?.[1])
  const [renewalStatus, setRenewalStatus] = useState<RenewalQuoteStatus>()
  const [winbackOption, setWinbackOption] = useState<RenewalOption>()
  const [error, setError] = useState<string>()
  const [isManual, setIsManual] = useState<boolean>(false)
  const [isRenewalWindowOpen, setIsRenewalWindowOpen] = useState<boolean>()
  const [isRenewalAccepted, setIsRenewalAccepted] = useState<boolean>(false)
  const [subSource, setSubSource] = useState<RenewalPlanSubSource>(RenewalPlanSubSource.subnav)
  const [source, setSource] = useState<RenewalPlanSource>(RenewalPlanSource.subnav)

  useEffect(() => {
    load()
  }, [])

  useEffect(() => {
    if (location?.state?.source) {
      setSource(location?.state?.source)
    } else if (location?.state?.prevUrl) {
      setSource(RenewalPlanSource.subnav)
    } else {
      setSource(RenewalPlanSource.email)
    }
    if (location?.state?.winbackOption) {
      setWinbackOption(location?.state?.winbackOption)
      setSelectedKey(BILLING_TABS.NEW_PLAN)
      setError("")
    }
  }, [location?.state])

  useEffect(() => {
    const filteredTabs = getTabs()

    if (filteredTabs.length > 2 && !location.pathname.split("billing/")?.[1]) {
      setSelectedKey(filteredTabs[0].key)
      History.replace({pathname: `/admin/billing/${filteredTabs[0].key}`, state: {}})
    }
  }, [renewalStatus, isManual, isRenewalWindowOpen])

  const load = () => {
    const businessId = authentication.business().uuid
    setLoaded(
      Promise.all([
        buService.getCurrentPlan({businessId}, {fetchPolicy: "network-only"}),
        salesService.getRenewalQuote(businessId, {fetchPolicy: "network-only"})
      ])
        .then(([plan, quote]) => {
          const statusToSet =
            quote.isManual && quote.status === RenewalQuoteStatus.Available ? RenewalQuoteStatus.SentForSignature : quote.status
          setRenewalStatus(statusToSet)
          setIsManual(quote.isManual)
          setIsRenewalWindowOpen(
            plan.window === ZuoraCurrentPlanWindow.RenewalWindow ||
              plan.window === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation ||
              (plan.window === ZuoraCurrentPlanWindow.Ongoing && plan.renewalPending) ||
              false
          )
          if (quote.errorType !== RenewalError.None) {
            intercom.addCustomMessage("")
          }
        })
        .catch((err: PBError) => {
          const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
          setError(message)
          intercom.addCustomMessage("")
        })
    )

    adminTracking.renewalPlan({
      stage: 1,
      type: RenewalPlanType.renewalFlow,
      page: RenewalPlanPage.currentplanStage,
      source
    })
  }

  const handleTabChange = (value: string) => {
    setError("")
    setSelectedKey(value)
    History.push({pathname: `/billing/${value}`, state: {}}, context)
  }

  const handleTimeout = () => {
    handleTabChange(BILLING_TABS.CURRENT_PLAN)
    load()
  }

  const handleCurrentPlanCallback: CurrentPlanCallback = (updatedSubSource) => {
    setSubSource(updatedSubSource)
    if (renewalStatus === RenewalQuoteStatus.Available) {
      handleTabChange(BILLING_TABS.NEW_PLAN)
    } else {
      handleTabChange(BILLING_TABS.RENEWAL_STATUS)
    }
  }

  const handleRenewalStatusCallback = () => {
    setRenewalStatus(RenewalQuoteStatus.SentForSignature)
    handleTabChange(BILLING_TABS.RENEWAL_STATUS)
    setIsRenewalAccepted(true)
  }

  const getTabs = () => {
    let allTabs = [
      {
        title: formatMessage({id: "admin.billing.tabs.chooseNewPlan"}),
        key: BILLING_TABS.NEW_PLAN,
        component: (
          <NewPlan
            winbackOption={winbackOption}
            onCallback={handleRenewalStatusCallback}
            onTimeout={handleTimeout}
            subSource={subSource}
            onBack={() => handleTabChange(BILLING_TABS.CURRENT_PLAN)}
            source={source}
          />
        ),
        activeBadge: true
      },
      {
        title: formatMessage({id: "admin.billing.tabs.renewalStatus"}),
        key: BILLING_TABS.RENEWAL_STATUS,
        component: (
          <RenewalStatus
            status={renewalStatus!}
            isManual={isManual}
            isRenewalAccepted={isRenewalAccepted}
            subSource={subSource}
            source={source}
          />
        )
      },
      {
        title: formatMessage({id: "admin.billing.tabs.currentPlan"}),
        key: BILLING_TABS.CURRENT_PLAN,
        component: (
          <CurrentPlan
            status={renewalStatus}
            isManual={isManual}
            onCallback={handleCurrentPlanCallback}
            onTimeout={handleTimeout}
            isRenewalWindowOpen={isRenewalWindowOpen}
            onCancel={() => setRenewalStatus(RenewalQuoteStatus.CancellationCreated)}
            source={source}
          />
        )
      },
      {
        title: formatMessage({id: "admin.billing.tabs.pastInvoices"}),
        key: BILLING_TABS.PAST_INVOICES,
        component: <Invoices />
      }
    ]
    if (!isRenewalWindowOpen || isManual || renewalStatus !== RenewalQuoteStatus.Available) {
      allTabs = allTabs.filter((tab) => tab.key !== BILLING_TABS.NEW_PLAN)
    }
    if (
      !isRenewalWindowOpen ||
      (!isManual &&
        renewalStatus !== RenewalQuoteStatus.SentForSignature &&
        renewalStatus !== RenewalQuoteStatus.SignedInProgress &&
        renewalStatus !== RenewalQuoteStatus.Completed)
    ) {
      allTabs = allTabs.filter((tab) => tab.key !== BILLING_TABS.RENEWAL_STATUS)
    }
    if (renewalStatus === RenewalQuoteStatus.CancellationCreated || renewalStatus === RenewalQuoteStatus.NotFound) {
      allTabs = allTabs.filter((tab) => tab.key !== BILLING_TABS.NEW_PLAN)
    }
    // add tab if dev toggle set & in renewal window
    if (devToggles.showYourPerkboxStory()) {
      allTabs = [
        ...allTabs,
        {
          title: formatMessage({id: "admin.billing.tabs.wrapped"}),
          key: BILLING_TABS.YOUR_PERKBOX_STORY,
          component: <Wrapped />
        }
      ]
    }

    return allTabs
  }

  return (
    <Loading load={loaded}>
      <H1 marginBottom={2}>
        <FormattedMessage id="admin.billing.tabs.title" />
      </H1>
      <Tabs
        id="adminBilling_currentPlan_tabs"
        addMarginTop
        unmountOnExit
        mountOnEnter
        onSelect={(value) => {
          setSubSource(RenewalPlanSubSource.subnav)
          handleTabChange(value)
        }}
        activeKey={selectedKey}
        tabs={getTabs()}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </Loading>
  )
}

Billing.contextTypes = {
  appPath: PropTypes.string
}

export default injectIntl(withRouter(Billing))
